import { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { api } from '../../context/AuthContext';
import { toast } from 'sonner';

const SCHEMA_TYPES = ['LocalBusiness', 'Article', 'FAQPage', 'Product', 'VideoObject', 'Organization', 'Event', 'Recipe'];

const BUSINESS_DATA_TEMPLATES = {
  LocalBusiness: {
    name: 'Acme Coffee',
    telephone: '+1-555-0100',
    address: {
      streetAddress: '123 Main St',
      addressLocality: 'Portland',
      addressRegion: 'OR',
      postalCode: '97201',
      addressCountry: 'US',
    },
    openingHours: 'Mo-Fr 07:00-18:00',
  },
  Article: {
    headline: 'How We Built It',
    author: { name: 'Jane Doe' },
    datePublished: '2026-01-15',
    image: 'https://example.com/cover.jpg',
  },
  FAQPage: {
    mainEntity: [
      { question: 'Do you ship internationally?', answer: 'Yes, to 40+ countries.' },
    ],
  },
  Product: {
    name: 'Acme Widget',
    description: 'A sturdy widget.',
    image: 'https://example.com/widget.jpg',
    brand: 'Acme',
  },
  VideoObject: {
    name: 'Product demo',
    description: 'A walkthrough of the product.',
    thumbnailUrl: 'https://example.com/thumb.jpg',
    uploadDate: '2026-01-15',
  },
  Organization: { name: 'Acme Inc.', logo: 'https://example.com/logo.png' },
  Event: {
    name: 'Acme Launch',
    startDate: '2026-06-01T18:00',
    location: { name: 'Portland Expo' },
  },
  Recipe: {
    name: 'Classic Pancakes',
    image: 'https://example.com/pancakes.jpg',
    recipeIngredient: ['2 cups flour', '2 eggs'],
    recipeInstructions: ['Mix.', 'Cook.'],
  },
};

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
};

export default function StructuredDataPage() {
  const [activeTab, setActiveTab] = useState('generate');
  const [url, setUrl] = useState('');
  const [schemaType, setSchemaType] = useState('LocalBusiness');
  const [businessDataText, setBusinessDataText] = useState(
    JSON.stringify(BUSINESS_DATA_TEMPLATES.LocalBusiness, null, 2),
  );
  const [businessDataTouched, setBusinessDataTouched] = useState(false);
  const [validateText, setValidateText] = useState('');
  const [status, setStatus] = useState('');
  const [data, setData] = useState(null);
  const [validation, setValidation] = useState(null);

  const [isLoading, setIsLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');

  const errDetail = (err) => {
    const d = err.response?.data?.detail;
    if (typeof d === 'string') return d;
    if (Array.isArray(d)) return d.map((x) => x.msg || x).join(', ');
    return err.message || 'Request failed';
  };

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    setHistoryError('');
    try {
      const { data: res } = await api.get('/structured-data/');
      setHistory(res.history || []);
    } catch (error) {
      setHistoryError(errDetail(error));
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const onSchemaTypeChange = (next) => {
    setSchemaType(next);
    if (!businessDataTouched) {
      setBusinessDataText(JSON.stringify(BUSINESS_DATA_TEMPLATES[next] || {}, null, 2));
    }
  };

  const parsedBusinessData = useMemo(() => {
    const text = businessDataText.trim();
    if (!text) return { value: {}, error: null };
    try {
      const value = JSON.parse(text);
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        return { value, error: null };
      }
      return { value: null, error: 'Business data must be a JSON object.' };
    } catch (e) {
      return { value: null, error: `Invalid JSON: ${e.message}` };
    }
  }, [businessDataText]);

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (parsedBusinessData.error) {
      setStatus(`Error: ${parsedBusinessData.error}`);
      return;
    }
    setIsLoading(true);
    setStatus('Generating schema...');
    setData(null);
    try {
      const { data: res } = await api.post('/structured-data/generate', {
        url,
        schema_type: schemaType,
        business_data: parsedBusinessData.value,
      });
      setStatus('Generated successfully!');
      setData(res);
      loadHistory();
    } catch (error) {
      setStatus(`Error: ${errDetail(error)}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDetect = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setStatus('Detecting schema...');
    setData(null);
    try {
      const { data: res } = await api.post('/structured-data/detect', { url });
      setStatus('Detected successfully!');
      setData(res);
    } catch (error) {
      setStatus(`Error: ${errDetail(error)}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleValidate = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setValidation(null);
    setStatus('Validating schema...');
    try {
      const { data: res } = await api.post('/structured-data/validate', {
        schema_str: validateText,
      });
      setValidation(res);
      setStatus(res.valid ? 'Schema is valid.' : 'Schema has issues.');
    } catch (error) {
      setStatus(`Error: ${errDetail(error)}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewHistory = (record) => {
    setData({
      schema: record.schema,
      json_ld: record.json_ld,
      schema_type: record.schema_type,
      id: record.id,
    });
    setUrl(record.url || '');
    setSchemaType(record.schema_type || 'LocalBusiness');
    if (record.business_data) {
      setBusinessDataText(JSON.stringify(record.business_data, null, 2));
      setBusinessDataTouched(true);
    }
    setStatus(`Loaded schema from ${formatDate(record.created_at)}`);
    setActiveTab('generate');
  };

  const copyLd = () => {
    if (!data?.json_ld) return;
    navigator.clipboard.writeText(data.json_ld);
    toast.success('JSON-LD copied');
  };

  const loadIntoValidator = () => {
    if (!data?.json_ld) return;
    setValidateText(data.json_ld);
    setValidation(null);
    setActiveTab('validate');
  };

  const tabs = [
    { id: 'generate', label: 'Generate' },
    { id: 'detect', label: 'Detect Existing' },
    { id: 'validate', label: 'Validate' },
    { id: 'history', label: `History${history.length > 0 ? ` (${history.length})` : ''}` },
  ];

  return (
    <motion.div className="space-y-6 p-4 md:p-6" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Structured Data (JSON-LD)</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1 text-sm">Generate, detect, or validate Schema.org markup for your pages.</p>
      </div>

      <div className="flex gap-4 border-b border-slate-200 dark:border-slate-800">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`pb-2 px-1 text-sm ${activeTab === t.id ? 'border-b-2 border-violet-600 font-medium text-violet-600' : 'text-slate-500'}`}
            onClick={() => {
              setActiveTab(t.id);
              if (t.id === 'history') loadHistory();
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6 shadow-sm">
        {(activeTab === 'generate' || activeTab === 'detect') && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Target URL</label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com"
              className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
              required
            />
          </div>
        )}

        {activeTab === 'generate' && (
          <form onSubmit={handleGenerate} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Schema Type</label>
              <select
                value={schemaType}
                onChange={(e) => onSchemaTypeChange(e.target.value)}
                className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
              >
                {SCHEMA_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Business Data (JSON)</label>
                <button
                  type="button"
                  onClick={() => {
                    setBusinessDataText(JSON.stringify(BUSINESS_DATA_TEMPLATES[schemaType] || {}, null, 2));
                    setBusinessDataTouched(false);
                  }}
                  className="text-xs text-violet-600 hover:text-violet-700"
                >
                  Reset to template
                </button>
              </div>
              <textarea
                value={businessDataText}
                onChange={(e) => {
                  setBusinessDataText(e.target.value);
                  setBusinessDataTouched(true);
                }}
                rows={10}
                spellCheck={false}
                className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs font-mono"
              />
              {parsedBusinessData.error && (
                <p className="mt-1 text-xs text-red-600">{parsedBusinessData.error}</p>
              )}
              <p className="mt-1 text-xs text-slate-500">Only fields present here will appear in the generated schema.</p>
            </div>
            <button
              type="submit"
              disabled={isLoading || !!parsedBusinessData.error}
              className="flex items-center gap-2 bg-violet-600 text-white px-4 py-2 rounded-md text-sm hover:bg-violet-700 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
              {isLoading ? 'Generating…' : 'Generate JSON-LD'}
            </button>
          </form>
        )}

        {activeTab === 'detect' && (
          <form onSubmit={handleDetect}>
            <button
              type="submit"
              disabled={isLoading}
              className="flex items-center gap-2 bg-violet-600 text-white px-4 py-2 rounded-md text-sm hover:bg-violet-700 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
              {isLoading ? 'Detecting…' : 'Detect Schema on Page'}
            </button>
          </form>
        )}

        {activeTab === 'validate' && (
          <form onSubmit={handleValidate} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">JSON-LD to validate</label>
              <textarea
                value={validateText}
                onChange={(e) => setValidateText(e.target.value)}
                rows={14}
                spellCheck={false}
                placeholder='{ "@context": "https://schema.org", "@type": "Organization", "name": "Acme", "url": "https://example.com" }'
                className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs font-mono"
                required
              />
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="flex items-center gap-2 bg-violet-600 text-white px-4 py-2 rounded-md text-sm hover:bg-violet-700 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
              {isLoading ? 'Validating…' : 'Validate'}
            </button>

            {validation && (
              <div className={`mt-2 p-3 rounded-md text-sm ${validation.valid ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300' : 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300'}`}>
                <p className="font-medium">{validation.valid ? 'Valid schema' : 'Validation failed'}</p>
                {validation.errors?.length > 0 && (
                  <ul className="mt-2 list-disc list-inside space-y-1">
                    {validation.errors.map((err, i) => <li key={`e-${i}`}>{err}</li>)}
                  </ul>
                )}
                {validation.warnings?.length > 0 && (
                  <div className="mt-2">
                    <p className="font-medium text-amber-700 dark:text-amber-300">Warnings</p>
                    <ul className="mt-1 list-disc list-inside space-y-1 text-amber-700 dark:text-amber-300">
                      {validation.warnings.map((w, i) => <li key={`w-${i}`}>{w}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </form>
        )}

        {activeTab === 'history' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200">Your Generated Schemas</h3>
              <button
                type="button"
                onClick={loadHistory}
                className="text-xs text-violet-600 hover:text-violet-700"
              >
                Refresh
              </button>
            </div>

            {historyLoading && <p className="text-sm text-slate-500">Loading…</p>}
            {historyError && (
              <div className="p-3 rounded-md text-sm bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300">
                {historyError}
              </div>
            )}
            {!historyLoading && !historyError && history.length === 0 && (
              <p className="text-sm text-slate-500">No schemas yet. Generate one to get started.</p>
            )}

            {history.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase text-slate-500 border-b border-slate-200 dark:border-slate-800">
                    <tr>
                      <th className="py-2 pr-4">Type</th>
                      <th className="py-2 pr-4">URL</th>
                      <th className="py-2 pr-4">Created</th>
                      <th className="py-2 pr-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((record) => (
                      <tr key={record.id} className="border-b border-slate-100 dark:border-slate-900">
                        <td className="py-2 pr-4">
                          <span className="px-2 py-0.5 text-xs rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                            {record.schema_type}
                          </span>
                        </td>
                        <td className="py-2 pr-4 text-slate-600 dark:text-slate-400 max-w-xs truncate" title={record.url}>{record.url}</td>
                        <td className="py-2 pr-4 text-slate-500">{formatDate(record.created_at)}</td>
                        <td className="py-2 pr-4 whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => handleViewHistory(record)}
                            className="text-violet-600 hover:text-violet-700"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {status && activeTab !== 'history' && (
          <div className={`mt-4 p-3 rounded-md text-sm ${status.startsWith('Error') ? 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300' : 'bg-violet-50 text-violet-800 dark:bg-violet-950/40 dark:text-violet-200'}`}>
            {status}
          </div>
        )}

        {data && activeTab !== 'history' && activeTab !== 'validate' && (
          <div className="mt-6">
            <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-2">Result</h3>
            <pre className="bg-slate-50 dark:bg-slate-900 p-4 rounded-md overflow-auto max-h-96 text-xs whitespace-pre-wrap text-slate-700 dark:text-slate-300">
              {data.json_ld || JSON.stringify(data.detected_schemas || data, null, 2)}
            </pre>
            {data.json_ld && (
              <div className="mt-2 flex gap-4 text-sm">
                <button type="button" onClick={copyLd} className="text-violet-600 hover:underline">
                  Copy JSON-LD
                </button>
                <button type="button" onClick={loadIntoValidator} className="text-violet-600 hover:underline">
                  Validate this
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}
